// handleGoalAdded.js
'use strict';
import {
  ADD_GOAL,
  ADD_LIST_ITEMS,
  MERGE_USER,
  MERGE_LIVE_STREAM_DATA,
  SET_LIVE_STREAM_DATA,
  MERGE_OPERATION_DATA,
} from '../ActionTypes.js';
import { API_JITTER_MSEC } from '../RemoteConfigKeys.js';

import addModal from '../action/addModal.js';
import removeModals from '../action/removeModals.js';
import setPrivateShowTimer from '../action/setPrivateShowTimer.js';
import closeModalAfterSeconds from '../action/closeModalAfterSeconds.js';
import groupPrivateHandler from '../action/groupPrivateHandler.js';
import updateTriggerPrivateUnlockState from '../action/updateTriggerPrivateUnlockState.js';
import exclusivePrivateHandler from '../action/exclusivePrivateHandler.js';

import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getOperationData from '../selector/getOperationData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getMeData from '../selector/getMeData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';

import { type, karaokeType } from '../resource/goalConstants.js';
import { privateShowTimerType } from '../resource/liveStreamConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import trackMixpanel from '../resource/mixpanel.js';
import { EventTypes } from '../resource/trackingConstants.js';

const AUTO_CLOSE_SHOW_POP_UP_SECONDS = 6; // TODO: remote config

/**
 * Handle goal added
 * @param {string} {goalId} - goal id.
 * @param {string} {userId} - user id, if goal event from private user channel.
 * @param {string} {streamId} - stream id, if goal event from private stream channel.
 * @kind action
 * @return {Promise} Action promise.
 */
const handleGoalAdded =
  ({ goalId, userId, streamId, ...goalData }) =>
  async (dispatch, getState) => {
    if (!goalId) return dispatch({ type: '' });
    const timestampOffsetSeconds = getTimestampOffset(
      getState(),
      Accuracy.SECOND
    );
    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    dispatch({
      type: ADD_GOAL,
      payload: {
        ...goalData,
        id: goalId,
      },
    });
    const goalType = goalData?.context?.type;
    if (goalType === type.KARAOKE) {
      // Backend didnt provide any type for toy device karaoke, i.e,lovense
      // Only way is to check with device object in context.
      const isToy = !!goalData?.context?.device;
      return dispatch({
        type: ADD_LIST_ITEMS,
        payload: {
          selectPath: [
            'liveStream',
            streamId,
            'goal',
            isToy ? karaokeType.TOY_DEVICE : karaokeType.DEFAULT,
          ],
          itemIds: [goalId],
        },
      });
    } else if (goalType === type.SHOW_FUNDING) {
      const fundingExp = goalData?.exp;
      if (fundingExp > now) {
        dispatch({
          type: SET_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'privateShow'],
            data: {
              fundingId: goalId,
              status: {
                isFunding: true,
              },
            },
          },
        });
        return dispatch(
          setPrivateShowTimer({
            streamId,
            timerType: privateShowTimerType.TO_FUNDING_FINAL_CALL,
          })
        );
      }
    } else if (goalType === type.SHOW) {
      const performNbf = goalData?.nbf;
      const activeStreamId = getOperationData(
        getState(),
        ['liveStream'],
        'activeStreamId'
      );
      if (performNbf > now && activeStreamId === streamId) {
        dispatch(
          removeModals({
            ids: ['PrivateShowBuyTicketModal'],
            transitionStatus: 'closing',
          })
        );
        dispatch(
          addModal({
            id: 'LiveStreamPrivateShowPopup',
          })
        );
        dispatch(
          closeModalAfterSeconds({
            modalId: 'LiveStreamPrivateShowPopup',
            seconds: Math.min(AUTO_CLOSE_SHOW_POP_UP_SECONDS, performNbf - now),
          })
        );
      }
      const existedTimerId = getOperationData(
        getState(),
        ['timer', 'livestream', streamId],
        'privateShow'
      );
      clearTimeout(existedTimerId);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['timer', 'livestream', streamId],
          data: {
            privateShow: null,
          },
        },
      });
      return dispatch({
        type: MERGE_LIVE_STREAM_DATA,
        payload: {
          selectPath: [streamId, 'privateShow'],
          data: {
            fundingId: goalData?.context?.funding_goal_id,
            performingId: goalId,
            status: {
              isPreparing: true,
            },
          },
        },
      });
    } else if (goalType === type.LOTTERY) {
      if (userId) {
        // User lottery event
        return dispatch({
          type: MERGE_USER,
          payload: {
            user: {
              id: userId,
              goal: {
                current: goalId,
              },
            },
          },
        });
      }
    } else if (goalType === type.TRIGGER_PRIVATE) {
      dispatch({
        type: MERGE_LIVE_STREAM_DATA,
        payload: {
          selectPath: [streamId, 'groupPrivate'],
          data: {
            currentId: goalId,
          },
        },
      });
      dispatch(groupPrivateHandler({ streamId }));
      setTimeout(
        () => {
          dispatch(
            updateTriggerPrivateUnlockState({
              streamId,
              goalId,
              needFetchGoalBreakdown: true,
            })
          );
        },
        Math.random() * getRemoteConfigData(getState(), API_JITTER_MSEC)
      );
    } else if (goalType === type.TRIGGER_EXCLUSIVE) {
      const meId = getMeData(getState(), 'id');
      const isGoalCreator =
        meId != null && meId === goalData?.context?.exclusive_to;
      if (isGoalCreator) {
        dispatch({
          type: MERGE_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'exclusivePrivate'],
            data: {
              currentId: goalId,
            },
          },
        });
        setTimeout(
          () => {
            dispatch(
              updateTriggerPrivateUnlockState({
                streamId,
                goalId,
                needFetchGoalBreakdown: true,
              })
            );
          },
          Math.random() * getRemoteConfigData(getState(), API_JITTER_MSEC)
        );
        return dispatch(exclusivePrivateHandler({ streamId }));
      }
    } else if (goalType === type.EXCLUSIVE) {
      const isExpired = goalData?.exp && goalData.exp <= now;
      if (isExpired) {
        return dispatch({ type: '' });
      }
      trackMixpanel({
        type: EventTypes.LIVESTREAM_EXCLUSIVE_PREPARING_TIMER,
        payload: {
          streamId,
          action: 'GOAL_RECEIVED',
          goalId,
          nbf: goalData?.nbf,
          receivedAtUnix: now,
          delayFromNbf: goalData?.nbf ? now - goalData.nbf : undefined,
        },
      });
      const payload = {
        currentId: goalId,
      };
      const currentId = getLiveStreamData(
        getState(),
        [streamId, 'exclusivePrivate'],
        'currentId'
      );
      if (currentId) {
        payload.lastId = currentId;
      }
      dispatch({
        type: MERGE_LIVE_STREAM_DATA,
        payload: {
          selectPath: [streamId, 'exclusivePrivate'],
          data: payload,
        },
      });
      return dispatch(exclusivePrivateHandler({ streamId }));
    }
    return dispatch({ type: '' });
  };

export default handleGoalAdded;
